import { ModelPath } from "./path";
import { ModelPathSet } from "./state";

export function configureValidationStep<TValue>(): IObjectValidationStepBuilder<TValue> {
	return new ValidationStepBuilder<TValue>(ModelPath.root);
}

export interface IValidationStep {
	getPaths(): ReadonlySet<ModelPath>;
}

type IValidationChildStepBuilder<TValue> = (builder: IObjectValidationStepBuilder<TValue>) => void;
type PropertyOrChildBuilder<TValue> = StringKeyOf<TValue> | IValidationChildStepBuilder<TValue>;

export type IValueValidationStepBuilder<TValue> =
	[TValue] extends [PrimitiveType] ? never :
	[TValue] extends [ReadonlyArray<unknown>] ? never :
	IObjectValidationStepBuilder<TValue>;

export interface IObjectValidationStepBuilder<TValue> extends IValidationStep {
	for<TProperty extends StringKeyOf<TValue>>(propertyName: TProperty): IValueValidationStepBuilder<TValue[TProperty]>;
	add(...properties: PropertyOrChildBuilder<TValue>[]): IObjectValidationStepBuilder<TValue>;
	whenType<TConditionalValue extends TValue>(typeGuardPredicate: (value: TValue) => value is TConditionalValue): IValueValidationStepBuilder<TConditionalValue>;
}

class ValidationStepBuilder<TValue> implements IObjectValidationStepBuilder<TValue> {
	private readonly _basePath: ModelPath;
	private readonly _children: ValidationStepBuilder<TValue[StringKeyOf<TValue>]>[];
	private readonly _paths: Set<ModelPath>;
	private _cachedResultPaths: Set<ModelPath> | undefined;
	private readonly _onAdd: (() => void) | undefined;

	constructor(basePath: ModelPath, onAdd?: () => void) {
		this._basePath = basePath;
		this._children = [];
		this._paths = new ModelPathSet();
		this._onAdd = onAdd;
	}

	public for<TProperty extends StringKeyOf<TValue>>(propertyName: TProperty): IValueValidationStepBuilder<TValue[TProperty]> {
		const childBuilder = new ValidationStepBuilder<TValue[TProperty]>(
			this._basePath.for(propertyName),
			(): void => {
				this._cachedResultPaths = undefined;
				this._onAdd?.();
			}
		);

		this._children.push(childBuilder);
		return childBuilder as unknown as IValueValidationStepBuilder<TValue[TProperty]>;
	}

	public whenType<TConditionalValue extends TValue>(
		_typeGuardPredicate: (value: TValue) => value is TConditionalValue
	): IValueValidationStepBuilder<TConditionalValue> {
		// NOTE: This method is only needed to access properties of union types.
		// The validation step builder does not perform any actual validation, but is only used to generate model paths.
		// Actual check for type predicate should happen in the validationExecution
		return this as unknown as IValueValidationStepBuilder<TConditionalValue>;
	}

	public add(...properties: PropertyOrChildBuilder<TValue>[]): IObjectValidationStepBuilder<TValue> {
		for (const property of properties) {
			if (typeof property === "function") {
				property(this);
			} else {
				const propertyPath: ModelPath = this._basePath.for(property);

				this._paths.add(propertyPath);
			}
		}

		this._cachedResultPaths = undefined;
		this._onAdd?.();

		return this;
	}

	public getPaths(): ReadonlySet<ModelPath> {
		if (this._cachedResultPaths) {
			return this._cachedResultPaths;
		}

		this._cachedResultPaths = new ModelPathSet(this._paths);
		for (const child of this._children) {
			for (const childPath of child.getPaths()) {
				this._cachedResultPaths.add(childPath);
			}
		}

		return this._cachedResultPaths;
	}
};